import React, { useState, useEffect } from "react";
import { Button, Table, Input, Space, message, Spin, Tag } from "antd";
import { FaPlus, FaEdit, FaSearch } from "react-icons/fa";
import { apiHandle } from "../../config/apiHandle";
import VariantModal from "./VariantModal";

const Variants = () => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);

  useEffect(() => {
    fetchVariants();
  }, []);

  const fetchVariants = async () => {
    setLoading(true);
    try {
      const response = await apiHandle.get("get-variants");
      setVariants(response?.data?.data || []);
    } catch (error) {
      message.error("Failed to fetch variants.");
    } finally {
      setLoading(false);
    }
  };
  // ✅ Add or Update Variant
  const handleSaveVariant = async (values) => {
    setLoading(true);
    try {
      if (editingVariant) {
        await apiHandle.put(
          `update-variant/${editingVariant._id}`,
          values
        );
        message.success("Variant updated successfully");
      } else {
        await apiHandle.post("add-variants", values);
        message.success("Variant added successfully");
      }

      fetchVariants();
      setIsModalVisible(false);
      setEditingVariant(null);
    } catch (error) {
      message.error("Error saving variant.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle Edit
  const handleEdit = (record) => {
    setEditingVariant(record);
    setIsModalVisible(true);
  };

  // ✅ Filter Variants
  const filteredVariants = variants.filter((variant) =>
    variant.name.toLowerCase().includes(search.toLowerCase())
  );

  // ✅ Table Columns
  const columns = [
    {
      title: "Variant Name",
      key: "name",
      dataIndex: "name",
    },
    {
      title: "Variant Values",
      key: "values",
      dataIndex: "values",
      render: (values) => (
        <>
          {values.map((value, index) => (
            <Tag color="blue" key={index}>
              {value}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Space>
          <Button icon={<FaEdit />} onClick={() => handleEdit(record)} />
        </Space>
      ),
    },
  ];

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h1 className="text-3xl font-bold text-center mb-4">
        Variant Management
      </h1>

      {/* ✅ Search Bar */}
      <div className="flex justify-between my-4">
        <Input
          placeholder="Search by Variant name"
          prefix={<FaSearch />}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: "300px" }}
        />
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => {
            setEditingVariant(null);
            setIsModalVisible(true);
          }}
        >
          Add Variant
        </Button>
      </div>

      {/* ✅ Table with Variants */}
      {loading ? (
        <Spin size="large" className="flex justify-center" />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredVariants}
          pagination={{ pageSize: 15 }}
          rowKey="_id"
        />
      )}

      {/* ✅ Reusable Modal */}
      <VariantModal
        visible={isModalVisible}
        onClose={() => {
          setIsModalVisible(false);
          setEditingVariant(null);
        }}
        onSave={handleSaveVariant}
        editingVariant={editingVariant}
      />
    </div>
  );
};

export default Variants;
